import React from 'react';
import { Avatar, Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { PublicTenantCompany } from '../../types/company';
import { Vehicle } from '../../types/vehicle';

// Presentational building blocks shared by public page templates (Classic, Minimal, ...).
// Each template is otherwise fully independent - these are optional, reusable primitives,
// not a required layout. A future template is free to not use any of these.

export const FooterEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
    <Box sx={{ width: 18, height: 3, borderRadius: 2, bgcolor: '#d4af37' }} />
    <Typography
      variant="overline"
      sx={{ fontWeight: 700, letterSpacing: 1.2, color: 'text.primary', lineHeight: 1 }}
    >
      {children}
    </Typography>
  </Stack>
);

export const FooterContactRow: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: 'action.selected',
        color: 'text.secondary',
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ pt: 0.5 }}>{children}</Box>
  </Stack>
);

export const FooterSocialButton: React.FC<{
  href: string;
  label: string;
  color: string;
  children: React.ReactNode;
}> = ({ href, label, color, children }) => (
  <IconButton
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    size="small"
    sx={{
      bgcolor: color,
      color: 'common.white',
      '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
    }}
  >
    {children}
  </IconButton>
);

export const SectionHeading: React.FC<{
  eyebrow: string;
  headline: string;
  subtitle?: string;
  light?: boolean;
}> = ({ eyebrow, headline, subtitle, light }) => (
  <Stack
    spacing={{ xs: 1.25, sm: 1.5 }}
    alignItems="center"
    sx={{
      textAlign: 'center',
      mb: { xs: 3, sm: 4 },
      width: '100%',
      maxWidth: { xs: '100%', md: 1040 },
      mx: 'auto',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 18, height: 3, borderRadius: 2, bgcolor: '#d4af37' }} />
      <Typography
        variant="overline"
        sx={{
          fontWeight: 700,
          letterSpacing: 1.2,
          color: light ? 'rgba(255,255,255,0.7)' : 'text.secondary',
        }}
      >
        {eyebrow}
      </Typography>
      <Box sx={{ width: 18, height: 3, borderRadius: 2, bgcolor: '#d4af37' }} />
    </Stack>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 800,
        color: light ? 'common.white' : 'text.primary',
        lineHeight: 1.15,
        fontSize: { xs: '1.5rem', sm: '1.875rem', md: '1.95rem', lg: '2.125rem' },
        whiteSpace: { md: 'nowrap' },
      }}
    >
      {headline}
    </Typography>
    {subtitle && (
      <Typography
        variant="body1"
        sx={{
          color: light ? 'rgba(255,255,255,0.75)' : 'text.secondary',
          maxWidth: 760,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Stack>
);

export const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
  icon,
  title,
  description,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 3,
      borderRadius: 3,
      height: '100%',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 12px 28px rgba(15,23,42,0.1)', transform: 'translateY(-2px)' },
    }}
  >
    <Avatar sx={{ bgcolor: 'rgba(212,175,55,0.15)', color: '#b8902a', width: 48, height: 48, mb: 2 }}>
      {icon}
    </Avatar>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Paper>
);

export const StepCard: React.FC<{ index: number; title: string; description: string; light?: boolean }> = ({
  index,
  title,
  description,
  light,
}) => (
  <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', maxWidth: 280, mx: 'auto' }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: '1.25rem',
        color: light ? '#0b0f19' : 'common.white',
        bgcolor: '#d4af37',
      }}
    >
      {index}
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: light ? 'common.white' : 'text.primary' }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: light ? 'rgba(255,255,255,0.75)' : 'text.secondary' }}>
      {description}
    </Typography>
  </Stack>
);

// The data/behavior every public page template receives. Each template is free to render
// this however it wants - use all of it, some of it, or structure it completely differently.
// The only thing templates have in common is this contract, not any shared layout.
export interface PublicTemplateProps {
  tenantCompany: PublicTenantCompany | undefined;
  tenantLogoUrl: string | null;
  logoLoadError: boolean;
  onLogoError: () => void;
  quickFleet: Vehicle[];
  tenantLocationSummary: string;
  tenantWhatsappHref: string | null;
  searchFilterCard: React.ReactNode;
  successAlert: React.ReactNode;
  renderResultBody: () => React.ReactNode;
  handleScrollToSearch: () => void;
  // True once a valid start/end date range is selected. Templates use this to fold away
  // the "browse the fleet" intro copy once the visitor has committed to dates, so the
  // actual (now-filtered) results sit closer to the search bar instead of below a wall of text.
  datesReady: boolean;
}
